/**
 * Free post-card picker in the cart drawer.
 *
 * Opens a panel over the cart body, takes a single design choice, adds that
 * variant, and re-renders the cart in place.
 *
 * Everything is delegated from the document rather than bound to elements,
 * because the drawer replaces its own innerHTML on every cart mutation — any
 * listener bound directly to a button inside it dies on the next re-render.
 *
 * The panel lives inside the drawer for that same reason: it is re-rendered
 * with the cart, so its subtotal and tiles can never drift from cart state.
 */
(function () {
  'use strict';

  const SELECTED_CLASS = 'is-selected';
  const OPEN_CLASS = 'cart-drawer--postcard-open';

  let busy = false;

  function panel() {
    return document.querySelector('[data-postcard-panel]');
  }

  function drawerRoot() {
    return document.querySelector('cart-drawer .drawer__inner') || document.querySelector('cart-drawer');
  }

  function openPanel() {
    const el = panel();
    const root = drawerRoot();
    if (!el || !root) return;
    el.hidden = false;
    root.classList.add(OPEN_CLASS);
  }

  function closePanel() {
    const el = panel();
    const root = drawerRoot();
    if (el) el.hidden = true;
    if (root) root.classList.remove(OPEN_CLASS);
    clearSelection();
  }

  function clearSelection() {
    const el = panel();
    if (!el) return;
    for (const tile of el.querySelectorAll('[data-postcard-tile]')) {
      tile.classList.remove(SELECTED_CLASS);
      tile.setAttribute('aria-checked', 'false');
    }
    setSubmitEnabled(false);
  }

  function selectTile(tile) {
    const el = panel();
    if (!el) return;
    for (const other of el.querySelectorAll('[data-postcard-tile]')) {
      const chosen = other === tile;
      other.classList.toggle(SELECTED_CLASS, chosen);
      other.setAttribute('aria-checked', chosen ? 'true' : 'false');
    }
    setSubmitEnabled(true);
  }

  function setSubmitEnabled(enabled) {
    const el = panel();
    const submit = el && el.querySelector('[data-postcard-submit]');
    if (!submit) return;
    submit.disabled = !enabled;
    submit.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  }

  function setBusy(state) {
    busy = state;
    const el = panel();
    const submit = el && el.querySelector('[data-postcard-submit]');
    if (!submit) return;
    const text = submit.querySelector('.cart-postcard-panel__submit-text');
    const loader = submit.querySelector('.cart-postcard-panel__submit-loader');
    if (text) text.hidden = state;
    if (loader) loader.hidden = !state;
    submit.disabled = state || submit.disabled;
  }

  function selectedVariantId() {
    const el = panel();
    const tile = el && el.querySelector('[data-postcard-tile].' + SELECTED_CLASS);
    return tile ? tile.getAttribute('data-variant-id') : null;
  }

  /**
   * Sections to refresh after the cart changes. Mirrors what cart-drawer.js
   * re-renders, minus its renderContents() — that re-opens the drawer as a
   * side effect, which fights the panel close animation.
   */
  function sectionTargets() {
    const list = [{ section: 'cart-drawer', host: '#CartDrawer', pick: '#CartDrawer' }];
    if (document.getElementById('cart-icon-bubble')) {
      list.push({ section: 'cart-icon-bubble', host: '#cart-icon-bubble', pick: '.shopify-section' });
    }
    return list;
  }

  function renderSections(targets, rendered) {
    if (!rendered) return;
    for (const { section, host, pick } of targets) {
      const html = rendered[section];
      const target = document.querySelector(host);
      if (!html || !target) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(pick);
      if (fresh) target.innerHTML = fresh.innerHTML;
    }
  }

  async function addPostcard() {
    const variantId = selectedVariantId();
    if (!variantId || busy) return;

    setBusy(true);
    const targets = sectionTargets();

    try {
      const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: targets.map((t) => t.section).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        showError(data && data.description);
        return;
      }

      // Closed before the re-render: the fresh markup ships the panel hidden,
      // and the open class lives on an element that survives it.
      closePanel();
      renderSections(targets, data.sections);
      notifyCartUpdated(data);
    } catch {
      showError();
    } finally {
      setBusy(false);
    }
  }

  /** Swaps the bin icon for a spinner so the tap has visible feedback. */
  function setRemoveBusy(button, state) {
    if (!button) return;
    const icon = button.querySelector('.cart-postcard-line__remove-icon');
    const loader = button.querySelector('.cart-postcard-line__remove-loader');
    if (icon) icon.style.display = state ? 'none' : '';
    if (loader) loader.hidden = !state;
    button.disabled = state;
  }

  async function removePostcard(lineKey, button) {
    if (!lineKey || busy) return;
    busy = true;
    setRemoveBusy(button, true);
    const targets = sectionTargets();

    try {
      const res = await fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: targets.map((t) => t.section).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Nothing was removed, so the row stays — restore the bin icon rather
        // than leaving a spinner running on a line that is still there.
        setRemoveBusy(button, false);
        return;
      }
      renderSections(targets, data.sections);
      notifyCartUpdated(data);
    } catch {
      setRemoveBusy(button, false);
    } finally {
      busy = false;
    }
  }

  /** Lets the rest of the theme (tier discount sync, upsells) react. */
  function notifyCartUpdated(cartData) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-postcard', cartData });
      }
    } catch {
      /* pub/sub is optional */
    }
  }

  function showError(message) {
    const el = panel();
    if (!el) return;
    let node = el.querySelector('.cart-postcard-panel__error');
    if (!node) {
      node = document.createElement('p');
      node.className = 'cart-postcard-panel__error';
      const footer = el.querySelector('.cart-postcard-panel__footer');
      if (footer) footer.insertBefore(node, footer.firstChild);
    }
    node.textContent = message || "Couldn't add the post card. Please try again.";
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-postcard-open]');
    if (open) {
      event.preventDefault();
      openPanel();
      return;
    }

    const back = event.target.closest('[data-postcard-close]');
    if (back) {
      event.preventDefault();
      closePanel();
      return;
    }

    const tile = event.target.closest('[data-postcard-tile]');
    if (tile) {
      event.preventDefault();
      selectTile(tile);
      return;
    }

    const submit = event.target.closest('[data-postcard-submit]');
    if (submit) {
      event.preventDefault();
      addPostcard();
      return;
    }

    const remove = event.target.closest('[data-postcard-remove]');
    if (remove) {
      event.preventDefault();
      removePostcard(remove.getAttribute('data-line-key'), remove);
    }
  });

  // Esc backs out of the picker rather than closing the whole cart.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const el = panel();
      if (!el || el.hidden) return;
      event.stopPropagation();
      closePanel();
    },
    true
  );
})();
